import { ReactComponent as CornerIcon } from '@assets/components/panel/monitoring/corner.svg';
import { IcMonitoringCardActive, IcMonitoringCardWorks } from '@assets/icons/20x20';
import { HStack, Icon, IconButton, Text, VStack } from '@chakra-ui/react';
import SensorIcon from '@components/map/popup/infos/sensors/SensorIcon';
import { ToastStatusEnum } from '@components/ui/toaster';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectSensorConfigurationById } from '@redux/situation/monitoring.selectors';
import { useUpdateSensorActivationMutation, useUpdateSensorMaintenanceMutation } from '@services/c2/c2.api';
import { getSensorIconKey } from '@utils/map/sensor.utils';
import {
  createSensorToast,
  getSensorHexColor,
  hasSpecificState,
  isSensorOrSubSensorConfigurationOfType,
} from '@utils/sensors/sensors.utils';
import { useIntl } from 'react-intl';

import { SubSensorTypeEnum } from '@/types/sensor/sensors';

import { SensorOrSubSensorInfo } from '../monitoring.types';
import { SortEnum, useMonitoringContext } from '../MonitoringContext';

type Props = {
  sensorOrSubSensorInfo: SensorOrSubSensorInfo;
  type: SortEnum;
};

function ActivationAndMaintenanceButtons({
  sensorOrSubSensorInfo,
}: Readonly<{ sensorOrSubSensorInfo: SensorOrSubSensorInfo }>) {
  const [patchActivation, { isLoading: isLoadingActive }] = useUpdateSensorActivationMutation();
  const [patchMaintenance, { isLoading: isLoadingMaintenance }] = useUpdateSensorMaintenanceMutation();
  const { configuration, active, maintenance } = sensorOrSubSensorInfo;

  const isSubSensor = 'sensorId' in configuration;
  const parentUniqueKey = isSubSensor ? configuration.sensorId : null;

  const parentConfiguration = useAppSelector((state) => selectSensorConfigurationById(state, false, parentUniqueKey));

  const toggleActive = () => {
    let params;
    const newActive = !active;
    if (isSubSensor && parentConfiguration) {
      params = {
        id: configuration.sensorId,
        site: parentConfiguration.site,
        appCode: parentConfiguration.appCode,
        active: newActive,
        type: configuration.type,
        codes: [configuration.code],
      };
    } else {
      params = {
        id: configuration.id,
        site: configuration.site,
        appCode: configuration.appCode,
        active: newActive,
      };
    }
    const toastParams = {
      state: newActive,
      commandValues: { state: newActive },
      command: 'sensors.commands.activationCommand',
      name: configuration.name,
    };
    createSensorToast({
      ...toastParams,
      toast: ToastStatusEnum.INFO,
    });

    patchActivation(params)
      .unwrap()
      .then(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.SUCCESS,
        }),
      )
      .catch(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.ERROR,
        }),
      );
  };

  const toggleMaintenance = () => {
    let params;
    const newMaintenance = !maintenance;
    if (isSubSensor && parentConfiguration) {
      params = {
        id: configuration.sensorId,
        site: parentConfiguration.site,
        appCode: parentConfiguration.appCode,
        maintenance: newMaintenance,
        type: configuration.type,
        codes: [configuration.code],
      };
    } else {
      params = {
        id: configuration.id,
        site: configuration.site,
        appCode: configuration.appCode,
        maintenance: newMaintenance,
      };
    }
    const toastParams = {
      commandValues: { state: newMaintenance },
      command: 'sensors.commands.maintenanceCommand',
      name: configuration.name,
    };
    createSensorToast({
      ...toastParams,
      toast: ToastStatusEnum.INFO,
    });
    patchMaintenance(params)
      .unwrap()
      .then(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.SUCCESS,
        }),
      )
      .catch(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.ERROR,
        }),
      );
  };

  return (
    <VStack gap={0.5} marginLeft="auto" flexShrink={0}>
      <IconButton
        variant="toggleIcon"
        data-active={sensorOrSubSensorInfo.active}
        color="blue"
        disabled={isLoadingActive}
        onClick={(e) => {
          e.stopPropagation();
          toggleActive();
        }}
      >
        <Icon as={IcMonitoringCardActive} boxSize="20px" />
      </IconButton>
      <IconButton
        variant="toggleIcon"
        data-active={sensorOrSubSensorInfo.maintenance}
        color="orange"
        disabled={isLoadingMaintenance}
        onClick={(e) => {
          e.stopPropagation();
          toggleMaintenance();
        }}
      >
        <Icon as={IcMonitoringCardWorks} boxSize="20px" />
      </IconButton>
    </VStack>
  );
}

export default function SensorCard({ sensorOrSubSensorInfo, type }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { setSelectedSensorKey } = useMonitoringContext();
  const { active, maintenance, configuration, status } = sensorOrSubSensorInfo;
  const isSegment = isSensorOrSubSensorConfigurationOfType(configuration, SubSensorTypeEnum.AUTOMATON_SEGMENT);

  const handleClick = () => {
    if (!isSegment) {
      setSelectedSensorKey(configuration.uniqueKey);
    }
  };

  const typeLabel = formatMessage({ id: `sensors.type.${configuration.type}` });

  const suffix = isSegment ? formatMessage({ id: `automatonSection.segment.${configuration.segmentType}` }) : null;
  const specificState = status && hasSpecificState(status) ? status.specificState : undefined;
  if (type === SortEnum.CRITICALITY) {
    return (
      <HStack
        gap={2}
        paddingLeft={2}
        paddingRight={1}
        height="88px"
        width="240px"
        position="relative"
        backgroundColor="neutral.600"
        justifyContent="start"
        cursor={isSegment ? 'default' : 'pointer'}
        onClick={handleClick}
      >
        <Icon as={CornerIcon} boxSize="6px" position="absolute" bottom={0} right={0} />
        <SensorIcon
          sensorIconName={getSensorIconKey(
            configuration.type,
            active,
            maintenance,
            specificState,
            status?.effectiveStatus,
            true,
          )}
          size="sm"
        />
        <VStack gap={0.5} alignItems="start">
          <Tooltip
            positioning={{
              placement: 'top',
            }}
            disabled={configuration.name.length <= 14}
            content={configuration.name}
          >
            <Text
              fontWeight="medium"
              backgroundColor="neutral.black"
              padding={0.25}
              fontSize="xs"
              lineClamp={1}
              wordBreak="break-all"
            >
              {configuration.name}
            </Text>
          </Tooltip>
          <Text fontSize="xs" color="neutral.200" lineClamp={2}>
            {typeLabel}
            {suffix ? ` - ${suffix}` : ''}
          </Text>
        </VStack>
        <ActivationAndMaintenanceButtons sensorOrSubSensorInfo={sensorOrSubSensorInfo} />
      </HStack>
    );
  }

  return (
    <HStack
      gap={0}
      height="88px"
      paddingRight={1}
      width="184px"
      position="relative"
      backgroundColor="neutral.600"
      borderLeft="8px solid"
      borderColor={getSensorHexColor(sensorOrSubSensorInfo)}
      cursor={isSegment ? 'default' : 'pointer'}
      onClick={handleClick}
    >
      <Icon as={CornerIcon} boxSize="6px" position="absolute" bottom={0} right={0} />
      <VStack paddingLeft={2} paddingRight={1} gap={0.5} alignItems="start">
        <Tooltip
          positioning={{
            placement: 'top',
          }}
          disabled={configuration.name.length <= 13}
          content={configuration.name}
        >
          <Text
            fontWeight="medium"
            backgroundColor="neutral.black"
            padding={0.25}
            fontSize="xs"
            lineClamp={1}
            wordBreak="break-all"
          >
            {configuration.name}
          </Text>
        </Tooltip>
        <Text fontSize="xs" color="neutral.200" lineClamp={2}>
          {typeLabel}
          {suffix ? ` - ${suffix}` : ''}
        </Text>
      </VStack>
      <ActivationAndMaintenanceButtons sensorOrSubSensorInfo={sensorOrSubSensorInfo} />
    </HStack>
  );
}
